import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Wordmark } from '@agnivo/ui';
import { getMessages } from '../i18n';
import { useApi } from '../api/ApiProvider';
import { useAppConfig } from '../config';
import { useNavExtra } from './navSlots';
import { useShellNavItems } from './shellNav';
import { ChatIcon, AgentsIcon, SettingsIcon } from './navIcons';
import { prefetchResource, AGENTS_ACTIVE_KEY, AGENTS_ALL_KEY, USAGE_KEY, fetchAgentsActive, fetchAgentsAll, fetchUsage, } from '../data/resourceCache';
const t = getMessages();
// testId: STABLE hooks for the browser-smoke suites (DS/SRE testability contract,
// same do-not-rename rule as AgentSelector's "agent-select") — quality-tests
// drives SPA-route nav through these; renaming breaks the e2e leg silently.
const NAV = [
    { to: '/', end: true, label: t.nav.chat, Icon: ChatIcon, testId: 'nav-chat' },
    { to: '/agents', end: false, label: t.nav.agents, Icon: AgentsIcon, testId: 'nav-agents' },
    { to: '/settings', end: false, label: t.nav.settings, Icon: SettingsIcon, testId: 'nav-settings' },
];
function navClass({ isActive }) {
    return [
        'flex flex-1 flex-col items-center gap-0.5 py-2 text-xs font-medium sm:flex-none sm:flex-row sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm sm:rounded-md',
        isActive ? 'text-accent sm:bg-accent/10' : 'text-muted hover:text-ink',
    ].join(' ');
}
/** One nav item — icon always; label alongside (stacked on the mobile tab bar,
 *  inline on the desktop header). Labels are kept on both (iOS/Android tab-bar
 *  convention: icon-only is less discoverable) — Dmitry review 2026-07-13. */
/** `where` disambiguates the two render sites: the nav renders in BOTH the
 *  mobile tab bar and the desktop header, and a duplicated data-testid trips
 *  Playwright strict mode. The tab bar carries the PLAIN names (nav-chat/…) —
 *  the mini-app smoke runs a mobile viewport; the desktop header gets a
 *  `-header` suffix. */
function navItems(where) {
    return NAV.map(({ to, end, label, Icon, testId }) => (_jsxs(NavLink, { to: to, end: end, className: navClass, "data-testid": where === 'tabs' ? testId : `${testId}-header`, children: [_jsx(Icon, { className: "h-5 w-5" }), _jsx("span", { children: label })] }, to)));
}
/**
 * Shell-registered sections (`shellNav.tsx`), rendered with the SAME classes and
 * testId convention as the built-ins so a registered item is indistinguishable
 * from a first-class tab — equal weight is the point (product ruling 2026-08-18:
 * «Мои ассистенты» is a top-level peer of «Чат», not a settings entry).
 */
function shellNavItems(items, where) {
    return items.map(({ to, end, label, icon: Icon, testId }) => (_jsxs(NavLink, { to: to, end: end, className: navClass, "data-testid": where === 'tabs' ? testId : `${testId}-header`, children: [_jsx(Icon, { className: "h-5 w-5" }), _jsx("span", { children: label })] }, to)));
}
/**
 * App shell — mobile-first: top header with the wordmark, bottom tab bar on
 * small screens that becomes inline header nav from `sm:` up. A SHELL may inject
 * chrome into the (desktop) header nav row via the NavExtra slot — desktop puts
 * the data-bridge pill there so it shares one visual context with the tabs.
 */
export function Layout() {
    const config = useAppConfig();
    const navExtra = useNavExtra();
    const shellItems = useShellNavItems();
    const api = useApi();
    const location = useLocation();
    // Prefetch every tab's data once, on shell mount — so the FIRST switch to any
    // tab is already warm (no loading blip). The shell persists across tab changes
    // (only the <Outlet/> swaps), so this runs once per session, not per switch.
    useEffect(() => {
        prefetchResource(AGENTS_ACTIVE_KEY, fetchAgentsActive(api.client));
        prefetchResource(AGENTS_ALL_KEY, fetchAgentsAll(api.client));
        prefetchResource(USAGE_KEY, fetchUsage(api.client));
    }, [api]);
    return (
    // overflow-hidden pins the shell to the viewport: the document itself never
    // scrolls, so the header + bottom tab bar stay put. All scrolling happens in
    // <main>. Without this, a natural-height screen (Agents/Settings) overflowed
    // and the webview scrolled the whole page → the tab bar slid up over the cards
    // (Dmitry mini-app bug 2026-07-14 #3).
    _jsxs("div", { className: "flex h-full flex-col overflow-hidden", children: [_jsxs("header", { className: "flex items-center gap-3 border-b border-border bg-card px-4 py-2.5", children: [_jsx(Wordmark, { size: 18 }), config.apiMode === 'mock' && (_jsx("span", { className: "rounded-full border border-border px-2 py-0.5 text-xs text-muted", title: t.chat.demoBadge, children: t.chat.demoBadge })), _jsxs("div", { className: "ml-auto flex items-center gap-2", children: [navExtra, _jsxs("nav", { "aria-label": t.appName, className: "hidden gap-1 sm:flex", children: [navItems('header'), shellNavItems(shellItems, 'header')] })] })] }), _jsx("main", { className: "min-h-0 flex-1 overflow-y-auto overscroll-contain", children: _jsx("div", { className: "ag-screen-enter h-full", children: _jsx(Outlet, {}) }, location.pathname) }), _jsxs("nav", { "aria-label": t.appName, className: "flex border-t border-border bg-card pb-[env(safe-area-inset-bottom)] sm:hidden", children: [navItems('tabs'), shellNavItems(shellItems, 'tabs')] })] }));
}
//# sourceMappingURL=Layout.js.map